import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Facebook, Instagram, Twitter, Youtube, Mail, MessageCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { socialMediaLinks } from "@/config/socialMedia";

const newsletterSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email is too long")
    .toLowerCase(),
});

type NewsletterForm = z.infer<typeof newsletterSchema>;

const Footer = () => {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NewsletterForm>({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data: NewsletterForm) => {
    try {
      // TODO: Implement newsletter subscription in database
      // For now, just show success message
      toast({
        title: "Subscribed!",
        description: "Thank you for subscribing to our newsletter.",
      });
      reset();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Subscription failed",
        description: "Please try again later.",
      });
    }
  };
  const footerLinks = {
    Shop: ["T-Shirts", "Hoodies", "Mugs", "Posters", "Phone Cases", "All Products"],
    Company: ["About Us", "Contact", "Careers", "Press Kit", "Blog"],
    Support: ["Help Center", "Shipping Info", "Returns", "Size Guide", "FAQ"],
    Legal: ["Privacy Policy", "Terms of Service", "Cookie Policy", "Refund Policy"],
  };

  return (
    <footer className="bg-gray-950 border-t border-neutral-800">
      {/* Newsletter Section */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-brand-cyan to-brand-magenta mb-4">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Stay in the Loop</h3>
            <p className="text-muted-foreground mb-6">
              Subscribe to get special offers, free giveaways, and exclusive deals.
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="flex-1">
                <Input
                  {...register("email")}
                  type="email"
                  placeholder="Enter your email"
                  disabled={isSubmitting}
                  className={errors.email ? "border-destructive" : ""}
                  maxLength={255}
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                )}
              </div>
              <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <div className="mb-4">
              <span className="text-2xl font-bold bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow bg-clip-text text-transparent">
                Aaaprinterz
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Premium print-on-demand products designed by creative partners worldwide.
            </p>
            <div className="flex gap-3">
              {socialMediaLinks.facebook.enabled && (
                <a
                  href={socialMediaLinks.facebook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialMediaLinks.facebook.label}
                  className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {socialMediaLinks.instagram.enabled && (
                <a
                  href={socialMediaLinks.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialMediaLinks.instagram.label}
                  className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialMediaLinks.twitter.enabled && (
                <a
                  href={socialMediaLinks.twitter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialMediaLinks.twitter.label}
                  className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {socialMediaLinks.youtube.enabled && (
                <a
                  href={socialMediaLinks.youtube.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialMediaLinks.youtube.label}
                  className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                >
                  <Youtube className="h-5 w-5" />
                </a>
              )}
              {socialMediaLinks.whatsapp.enabled && (
                <a
                  href={socialMediaLinks.whatsapp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialMediaLinks.whatsapp.label}
                  className="h-10 w-10 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center justify-center"
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-semibold mb-4">{title}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground">
            © 2024 Aaaprinterz. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png"
              alt="Visa"
              className="h-6 opacity-50 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png"
              alt="Mastercard"
              className="h-6 opacity-50 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/2560px-PayPal.svg.png"
              alt="PayPal"
              className="h-6 opacity-50 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
